import { useState } from 'react'
import { BookOpen, Pencil, Trash2 } from 'lucide-react'
import type { NtCourseExemplar, NtBlock } from '../../core/types'
import {
  useCourseExemplars, saveCourseExemplar, deleteCourseExemplar, costTotal, type CourseExemplarContent,
} from './model'
import { DayPlanner } from './DayPlanner'
import { ArtifactEditor } from './ArtifactOptions'
import { Text, Area, Select } from '../../ui/Field'
import { Empty, ErrorState } from '../../ui/States'
import { ArtifactList } from './ArtifactOptions'

/**
 * Course exemplars — what a strong NorseTerm course looks like.
 *
 * Kept apart from the real catalog on purpose: students rank from the catalog,
 * so it must only ever hold real courses. Exemplars are for teachers, read
 * beside the form while they write their own (the panel below), and curated
 * by whoever manages the term (Setup → Course Exemplars).
 */

/** One exemplar, read-only: the description, why it works, the week, the costs. */
function ExemplarBody({ e }: { e: NtCourseExemplar }) {
  return (
    <div style={{ marginTop: 'var(--s-3)' }}>
      <p style={{ fontSize: 'var(--t-sm)', color: 'var(--ink-2)', margin: '0 0 var(--s-2)' }}>
        <strong>{e.good ? 'Why it works:' : 'Why it would be sent back:'}</strong> {e.why}
      </p>
      <p style={{ fontSize: 'var(--t-sm)', color: 'var(--ink-2)', whiteSpace: 'pre-wrap', margin: '0 0 var(--s-2)' }}>{e.desc}</p>
      <p className="work__sub" style={{ margin: '0 0 var(--s-2)' }}>
        {[e.room, e.block && `${e.block} block`, `${e.min}–${e.cap} students`,
          e.costItems.length ? `$${costTotal(e.costItems)} (${e.costItems.map((c) => c.label).join(', ')})` : 'No cost']
          .filter(Boolean).join(' · ')}
      </p>
      {(e.artifactOptions ?? []).length > 0 && (
        <div style={{ margin: '0 0 var(--s-2)' }}>
          <div className="opt__label">Artifact options</div>
          <ArtifactList options={e.artifactOptions} />
        </div>
      )}
      {e.plan.length > 0 && (
        <DayPlanner plan={e.plan} readOnly onChange={() => {}} session={e.block === 'PM' ? 1 : 0}
          basis={e.block ? 'example' : 'either'} />
      )}
    </div>
  )
}

/* ---------------- beside the teacher's course form ---------------- */

export function CourseExemplarPanel({ defaultOpen = false }: { defaultOpen?: boolean }) {
  const rows = useCourseExemplars()
  const [show, setShow] = useState(defaultOpen)
  const [open, setOpen] = useState<string | null>(null)
  if (!rows.length) return null
  return (
    <section className="card" style={{ margin: 'var(--s-3) 0' }}>
      <button className="cat__head" onClick={() => setShow((s) => !s)} aria-expanded={show}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-2)' }}>
          <BookOpen size={16} aria-hidden="true" />
          <span style={{ fontWeight: 620 }}>Example courses</span>
          <span className="work__sub">{rows.length} sample courses with their full three-day outline. Open one to read it.</span>
        </div>
        <span className="pill">{show ? 'Hide' : 'Show'}</span>
      </button>
      {show && (
        <ul className="work" style={{ marginTop: 'var(--s-3)' }}>
          {rows.map((e) => (
            <li key={e.id} className="work__row" style={{ display: 'block' }}>
              <button className="cat__head" onClick={() => setOpen(open === e.id ? null : e.id)} aria-expanded={open === e.id}>
                <span style={{ fontWeight: 610 }}>{e.title}</span>
                <span className={`pill ${e.good ? 'pill--good' : 'pill--serious'}`}>{e.good ? 'Strong' : 'Would be sent back'}</span>
              </button>
              {open === e.id && <ExemplarBody e={e} />}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

/* ---------------- Setup → Course Exemplars ---------------- */

export function CourseExemplarsAdmin({ who }: { who: string }) {
  const rows = useCourseExemplars()
  const [editing, setEditing] = useState<NtCourseExemplar | 'new' | null>(null)
  const [open, setOpen] = useState<string | null>(null)

  if (editing) {
    return <Form initial={editing === 'new' ? undefined : editing} who={who} onDone={() => setEditing(null)} />
  }
  return (
    <>
      <div className="notice notice--info" style={{ marginTop: 'var(--s-2)' }}>
        <span aria-hidden="true">i</span>
        <div>
          Teachers see these beside their course form while they write. They never appear in the course
          catalog students make their course selections from, which only ever holds real courses.
        </div>
      </div>
      <button className="btn" style={{ width: 'auto' }} onClick={() => setEditing('new')}>Add an example course</button>
      {rows.length === 0 ? (
        <Empty title="No example courses yet" body="A few strong ones from past years are the best start." />
      ) : (
        <ul className="work" style={{ marginTop: 'var(--s-3)' }}>
          {rows.map((e) => (
            <Row key={e.id} e={e} open={open === e.id} onToggle={() => setOpen(open === e.id ? null : e.id)}
              onEdit={() => setEditing(e)} />
          ))}
        </ul>
      )}
    </>
  )
}

function Row({ e, open, onToggle, onEdit }: { e: NtCourseExemplar; open: boolean; onToggle: () => void; onEdit: () => void }) {
  const [confirming, setConfirming] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  return (
    <li className="work__row" style={{ alignItems: 'flex-start' }}>
      <div className="work__main">
        <button className="row-link" onClick={onToggle} aria-expanded={open}>{e.title}</button>
        <div className="work__sub">{e.good ? 'Strong' : 'Would be sent back'} · {e.block ?? 'either block'} · {e.room || 'no room'}</div>
        {open && <ExemplarBody e={e} />}
        {err && <ErrorState body={err} />}
        {confirming && (
          <div className="opt__bar" style={{ marginTop: 'var(--s-2)' }}>
            <button className="btn" style={{ width: 'auto' }}
              onClick={() => deleteCourseExemplar(e.id).catch((x) => setErr(x instanceof Error ? x.message : String(x)))}>
              Yes, delete it
            </button>
            <button className="btn btn--ghost" style={{ width: 'auto' }} onClick={() => setConfirming(false)}>Keep it</button>
          </div>
        )}
      </div>
      <button className="opt__icon" aria-label={`Edit ${e.title}`} onClick={onEdit}><Pencil size={15} aria-hidden="true" /></button>
      <button className="opt__icon opt__icon--danger" aria-label={`Delete ${e.title}`} onClick={() => setConfirming(true)}>
        <Trash2 size={15} aria-hidden="true" />
      </button>
    </li>
  )
}

const BLOCKS: Array<[NtBlock | null, string]> = [[null, 'Either'], ['AM', 'Morning'], ['PM', 'Afternoon']]

function Form({ initial, who, onDone }: { initial?: NtCourseExemplar; who: string; onDone: () => void }) {
  const [f, setF] = useState<CourseExemplarContent>({
    title: initial?.title ?? '', good: initial?.good ?? true, why: initial?.why ?? '', desc: initial?.desc ?? '',
    needs: initial?.needs ?? '', room: initial?.room ?? '', block: initial?.block ?? null,
    cap: initial?.cap ?? 16, min: initial?.min ?? 6, costItems: initial?.costItems ?? [],
    artifactOptions: initial?.artifactOptions ?? [], plan: initial?.plan ?? [],
  })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const set = <K extends keyof CourseExemplarContent>(k: K, v: CourseExemplarContent[K]) => setF((x) => ({ ...x, [k]: v }))

  const save = async (ev: React.FormEvent) => {
    ev.preventDefault()
    setBusy(true); setErr(null)
    try {
      await saveCourseExemplar(initial?.id, { ...f, title: f.title.trim(), why: f.why.trim(), desc: f.desc.trim() }, who)
      onDone()
    } catch (x) { setErr(x instanceof Error ? x.message : String(x)); setBusy(false) }
  }

  return (
    <form onSubmit={save} className="card" style={{ marginTop: 'var(--s-2)' }}>
      <button type="button" className="backlink" onClick={onDone}
        style={{ background: 'none', border: 0, cursor: 'pointer', padding: 0 }}>← Example courses</button>
      <h2 style={{ fontSize: 'var(--t-md)', margin: '0 0 var(--s-3)' }}>{initial ? 'Edit example course' : 'Add an example course'}</h2>
      {err && <ErrorState body={`Not saved. ${err}`} />}

      <fieldset style={{ border: 0, padding: 0, margin: '0 0 var(--s-4)' }}>
        <legend style={{ fontSize: 'var(--t-sm)', fontWeight: 600, color: 'var(--ink-2)', padding: 0 }}>This example is</legend>
        <div style={{ display: 'grid', gap: 4, marginTop: 'var(--s-2)' }}>
          {([[true, 'Strong: a model to follow'], [false, 'Weak: one that would be sent back']] as const).map(([v, label]) => (
            <label key={String(v)} className="pick">
              <input type="radio" name="cgood" checked={f.good === v} onChange={() => set('good', v)} />
              <span className="pick__label">{label}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <Text label="Course title" value={f.title} onChange={(v) => set('title', v)} required />
      <Area label={f.good ? 'Why it works' : 'Why it would be sent back'} value={f.why} onChange={(v) => set('why', v)} required />
      <Area label="Description" value={f.desc} onChange={(v) => set('desc', v)} />
      <Text label="Room or location" value={f.room} onChange={(v) => set('room', v)} />
      <div className="grid2">
        <Text label="Minimum" type="number" value={String(f.min)} onChange={(v) => set('min', Number(v) || 0)} />
        <Text label="Cap" type="number" value={String(f.cap)} onChange={(v) => set('cap', Number(v) || 1)} />
      </div>
      <Select label="Block" value={BLOCKS.find(([b]) => b === f.block)?.[1] ?? 'Either'}
        options={BLOCKS.map(([, l]) => l)} onChange={(v) => set('block', BLOCKS.find(([, l]) => l === v)?.[0] ?? null)} />
      <Area label="What has to be arranged" value={f.needs} onChange={(v) => set('needs', v)} />
      {f.costItems.length > 0 && (
        <p className="opt__hint">Cost: ${costTotal(f.costItems)} ({f.costItems.map((c) => `${c.label} ($${c.amount})`).join(', ')})</p>
      )}
      <ArtifactEditor options={f.artifactOptions ?? []} onChange={(v) => set('artifactOptions', v)} />
      <DayPlanner plan={f.plan} onChange={(plan) => set('plan', plan)} session={f.block === 'PM' ? 1 : 0}
        basis={f.block ? 'example' : 'either'} />

      <button className="btn" type="submit" disabled={busy || !f.title.trim() || !f.why.trim()} style={{ marginTop: 'var(--s-4)' }}>
        {busy ? 'Saving…' : 'Save'}
      </button>
    </form>
  )
}
